import React, { useEffect, useState } from 'react';
import {
  Loader2, Plus, Trash2, Pencil, Check, X, Merge, Tag as TagIcon,
} from 'lucide-react';
import { sopApi } from '../api';
import TagChip from '../components/TagChip';

/**
 * Keeping the tag bucket clean is what keeps search working: duplicates split
 * the shelf, and a tag nobody recognises is a tag nobody clicks. Hence rename
 * (propagates everywhere), merge (folds one into another and keeps the old name
 * as an alias), and aliases (extra words that resolve to the same tag).
 */
const TagManager = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newTag, setNewTag] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [aliasFor, setAliasFor] = useState(null);
  const [aliasText, setAliasText] = useState('');
  const [mergeFrom, setMergeFrom] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await sopApi.listTags();
      setTags(r.data || []);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); await load(); }
    catch (err) { setError(err.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const createTag = (e) => {
    e.preventDefault();
    if (!newTag.trim()) return;
    run(async () => { await sopApi.createTag({ name: newTag.trim() }); setNewTag(''); });
  };

  const saveRename = (tag) => {
    if (!editName.trim() || editName.trim() === tag.name) { setEditingId(null); return; }
    run(async () => { await sopApi.updateTag(tag.id, { name: editName.trim() }); setEditingId(null); });
  };

  const addAlias = (tag) => {
    if (!aliasText.trim()) return;
    run(async () => { await sopApi.addTagAlias(tag.id, aliasText.trim()); setAliasFor(null); setAliasText(''); });
  };

  const doMerge = (targetId) => {
    run(async () => { await sopApi.mergeTag(mergeFrom.id, targetId); setMergeFrom(null); });
  };

  const remove = (tag) => {
    if (!window.confirm(
      `Delete "${tag.name}"? It will be removed from ${tag.usageCount} SOP${tag.usageCount === 1 ? '' : 's'}.`
    )) return;
    run(async () => { await sopApi.deleteTag(tag.id); });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading tags…
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-100">Tag Manager</h2>
        <p className="text-sm text-slate-400 mt-1">
          Tags drive search ranking. Merge duplicates and add aliases so people find SOPs
          using whatever word they actually say.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-sm">{error}</div>
      )}

      <form onSubmit={createTag} className="flex gap-2">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          placeholder="New tag name…"
          className="flex-1 bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm
            text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="submit"
          disabled={busy || !newTag.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
            text-white rounded-md text-sm font-medium disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Add
        </button>
      </form>

      {mergeFrom && (
        <div className="p-4 bg-slate-800 border border-indigo-500/40 rounded-lg space-y-3">
          <p className="text-sm text-slate-200">
            Merge <strong>{mergeFrom.name}</strong> into which tag? Its SOPs move over and
            &ldquo;{mergeFrom.name}&rdquo; is kept as an alias.
          </p>
          <div className="flex flex-wrap gap-2">
            {tags.filter((t) => t.id !== mergeFrom.id).map((t) => (
              <TagChip key={t.id} tag={t} size="xs" count={t.usageCount} onClick={() => doMerge(t.id)} />
            ))}
          </div>
          <button
            type="button"
            onClick={() => setMergeFrom(null)}
            className="text-xs text-slate-400 hover:text-slate-200 underline"
          >
            Cancel
          </button>
        </div>
      )}

      {tags.length === 0 ? (
        <div className="text-center py-16">
          <TagIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No tags yet.</p>
          <p className="text-sm text-slate-500 mt-1">They&rsquo;re created automatically as SOPs are added.</p>
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-lg divide-y divide-slate-700">
          {tags.map((tag) => (
            <div key={tag.id} className="p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                {editingId === tag.id ? (
                  <>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); saveRename(tag); }
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-sm text-slate-100"
                      autoFocus
                    />
                    <button type="button" onClick={() => saveRename(tag)} disabled={busy}
                      className="p-1.5 rounded text-emerald-400 hover:bg-slate-700" aria-label="Save">
                      <Check className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => setEditingId(null)}
                      className="p-1.5 rounded text-slate-400 hover:bg-slate-700" aria-label="Cancel">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <TagChip tag={tag} size="sm" />
                    <span className="text-xs text-slate-500">
                      {tag.usageCount} SOP{tag.usageCount === 1 ? '' : 's'}
                    </span>
                  </>
                )}

                <div className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => { setEditingId(tag.id); setEditName(tag.name); }}
                    disabled={busy}
                    className="p-1.5 rounded text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                    aria-label={`Rename ${tag.name}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setMergeFrom(tag)}
                    disabled={busy}
                    className="p-1.5 rounded text-slate-400 hover:text-indigo-300 hover:bg-slate-700"
                    aria-label={`Merge ${tag.name}`}
                  >
                    <Merge className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(tag)}
                    disabled={busy}
                    className="p-1.5 rounded text-slate-400 hover:text-rose-300 hover:bg-slate-700"
                    aria-label={`Delete ${tag.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pl-1">
                <span className="text-xs text-slate-500">Also matches:</span>
                {tag.aliases.length === 0 && <span className="text-xs text-slate-600">nothing yet</span>}
                {tag.aliases.map((alias) => (
                  <span
                    key={alias}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-slate-900 border border-slate-700 text-slate-400"
                  >
                    {alias}
                  </span>
                ))}
                {aliasFor === tag.id ? (
                  <span className="inline-flex items-center gap-1">
                    <input
                      type="text"
                      value={aliasText}
                      onChange={(e) => setAliasText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addAlias(tag); }
                        if (e.key === 'Escape') { setAliasFor(null); setAliasText(''); }
                      }}
                      placeholder="e.g. rf gun"
                      className="bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-xs text-slate-100 w-32"
                      autoFocus
                    />
                    <button type="button" onClick={() => addAlias(tag)} disabled={busy}
                      className="p-1 rounded text-emerald-400 hover:bg-slate-700" aria-label="Add alias">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setAliasFor(tag.id); setAliasText(''); }}
                    className="text-[11px] text-indigo-400 hover:text-indigo-300 inline-flex items-center gap-0.5"
                  >
                    <Plus className="w-3 h-3" /> alias
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagManager;
